import { Box, Typography } from '@mui/material';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useRecoilValue } from 'recoil';
import ConfirmationDialog from '~/components/Dialogs/ConfirmationDialog';
import DoseItem from '~/components/DoseItem/DoseItem';
import SelectableListItem from '~/components/SelectableListItem/SelectableListItem';
import { DoseMatrix } from '~/domain/patient/doseMatrix';
import useAvailableDoses from '~/hooks/dose/useAvailableDoses';
import useDeleteDataset from '~/hooks/useDeleteDataset';
import useViewportEnvironment from '~/hooks/useViewportEnvironment';
import { currentDoseState } from '~/state/atoms/dose/doseAtoms';
import { selectedPatientIdState } from '~/state/atoms/patientAtoms';

export default function DoseList() {
    const intl = useIntl();

    const availableDoses = useAvailableDoses(true);
    const currentDose = useRecoilValue(currentDoseState);
    const selectedPatientId = useRecoilValue(selectedPatientIdState);
    const { setEnvironmentDose } = useViewportEnvironment();
    const { deleteDose } = useDeleteDataset();

    const [deleteTarget, setDeleteTarget] = useState<DoseMatrix | null>(null);

    const handleConfirmDelete = () => {
        if (deleteTarget) {
            deleteDose(deleteTarget);
        }
        setDeleteTarget(null);
    };

    if (availableDoses.length === 0) {
        return null;
    }

    return (
        <>
            <Box>
                <Box px={1.5} pt={1.5} pb={1}>
                    <Typography fontSize={12} color="primary" fontWeight={500}>
                        <FormattedMessage id="doses" />
                    </Typography>
                </Box>
                {availableDoses.map((dose) => {
                    const isSelected =
                        currentDose?.dicomUid === dose.sopInstanceUID;
                    return (
                        <Box
                            key={dose.sopInstanceUID}
                            borderTop="1px solid"
                            borderColor="divider"
                        >
                            <SelectableListItem
                                isSelected={isSelected}
                                onClick={
                                    dose.item
                                        ? () =>
                                              setEnvironmentDose(
                                                  [dose.item as DoseMatrix],
                                                  selectedPatientId ?? ''
                                              )
                                        : undefined
                                }
                                disabled={!dose.isLoaded}
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    gap: 1,
                                }}
                            >
                                {dose.item ? (
                                    <DoseItem
                                        dose={dose.item}
                                        label={dose.label ?? 'Dose'}
                                        onDelete={() =>
                                            setDeleteTarget(dose.item!)
                                        }
                                    />
                                ) : (
                                    <Typography
                                        fontSize={12}
                                        color="text.secondary"
                                    >
                                        {dose.label ?? 'Dose'}
                                    </Typography>
                                )}
                            </SelectableListItem>
                        </Box>
                    );
                })}
            </Box>

            <ConfirmationDialog
                open={deleteTarget !== null}
                title={intl.formatMessage({ id: 'deleteDose' })}
                message={intl.formatMessage({ id: 'deleteDoseConfirm' })}
                onConfirm={handleConfirmDelete}
                onCancel={() => setDeleteTarget(null)}
                mode="error"
            />
        </>
    );
}
